import { useEffect, useState } from "react";
import api, { STATUS_META, STATUS_CODES, APPROVAL_STATUS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import TimesheetCalendar from "../components/TimesheetCalendar";
import ConversationSheet from "../components/ConversationSheet";
import { Button } from "../components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Send, ChevronLeft, ChevronRight, Loader2, MessageSquare } from "lucide-react";
import { toast, Toaster } from "sonner";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(4);
  const [initialized, setInitialized] = useState(false);
  const [ts, setTs] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [convoOpen, setConvoOpen] = useState(false);

  // On first mount, ask backend which month the user should land on
  useEffect(() => {
    if (!user) return;
    api.get("/timesheet/my/next-pending")
      .then(r => { setYear(r.data.year); setMonth(r.data.month); })
      .catch(() => {})
      .finally(() => setInitialized(true));
  }, [user]);

  const load = async () => {
    if (!user) return;
    const { data } = await api.get(`/timesheet/${user.id}/${year}/${month}`);
    setTs(data);
    setDirty(false);
  };

  useEffect(() => { if (initialized) load(); /* eslint-disable-next-line */ }, [user, year, month, initialized]);

  const onChange = (entries) => {
    setTs({ ...ts, entries });
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/timesheet/entries", { year, month, entries: ts.entries });
      setTs({ ...data, user: ts.user });
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (dirty) await save();
    setBusy(true);
    try {
      await api.post("/timesheet/submit", { timesheet_id: ts.id, message: "" });
      toast.success("Submitted for approval");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  const locked = ts && (ts.status === "submitted" || ts.status === "approved");

  const stepMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    if (y < 2026 || (y === 2026 && m < 4)) return;
    setMonth(m); setYear(y);
  };

  return (
    <Layout>
      <Toaster theme="dark" position="top-right" />
      <div className="p-6 lg:p-10 max-w-[1400px] mx-auto fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-blue-600 dark:text-blue-400 mb-2">— My Timesheet</p>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-light">{MONTHS[month-1]}<span className="text-muted-foreground"> · {year}</span></h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => stepMonth(-1)} data-testid="month-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-36 bg-background border-border" data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m,i) => <SelectItem key={m} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28 bg-background border-border" data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[2026,2027,2028].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={() => stepMonth(1)} data-testid="month-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>

        {ts && (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <StatusPill status={ts.status} />
                {locked && <span className="text-xs text-muted-foreground font-mono">Locked — awaiting admin action</span>}
                {dirty && <span className="text-xs text-amber-400 font-mono">Unsaved changes</span>}
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setConvoOpen(true)} data-testid="open-conversation">
                    <MessageSquare className="h-4 w-4 mr-1.5" /> Conversation {ts.conversation?.length ? `(${ts.conversation.length})` : ""}
                  </Button>
                  {!locked && <Button onClick={save} disabled={busy || !dirty} variant="outline" data-testid="save-button">
                    {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save
                  </Button>}
                  {!locked && <Button onClick={submit} disabled={busy} className="bg-blue-500 hover:bg-blue-400 text-white" data-testid="submit-button">
                    {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} <Send className="h-4 w-4 mr-1.5" /> Submit for Approval
                  </Button>}
                </div>
              </div>

              <TimesheetCalendar year={year} month={month} entries={ts.entries} onChange={onChange} locked={locked} />
            </div>

            <aside className="space-y-4">
              <MonthlySummary summary={ts.summary} />
              <Legend />
            </aside>
          </div>
        )}

        {ts && <ConversationSheet open={convoOpen} onOpenChange={setConvoOpen} ts={ts} onUpdated={load} canPost={!locked || ts.status === "clarification" || true} role="employee" />}
      </div>
    </Layout>
  );
}

export function StatusPill({ status }) {
  const meta = APPROVAL_STATUS[status] || APPROVAL_STATUS.draft;
  return (
    <span data-testid={`status-pill-${status}`} className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-[0.18em]"
      style={{ color: meta.color, backgroundColor: `${meta.color}1a`, border: `1px solid ${meta.color}55` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
}

export function MonthlySummary({ summary }) {
  if (!summary) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">Monthly Summary</p>
      <div className="text-5xl font-display font-light text-blue-600 dark:text-blue-400">
        {summary.working_days}
        <span className="text-xs font-mono uppercase text-muted-foreground tracking-widest ml-2">working days</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        {STATUS_CODES.map(c => (
          <div key={c} className="flex items-center justify-between px-2.5 py-2 rounded-md border" style={{ borderColor: STATUS_META[c].border, background: STATUS_META[c].bg }}>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs" style={{ color: STATUS_META[c].color }}>{c}</span>
              <span className="text-[11px] text-foreground/90">{STATUS_META[c].label.split("(")[0].trim()}</span>
            </div>
            <span className="font-mono text-sm" style={{ color: STATUS_META[c].color }} data-testid={`monthly-count-${c}`}>{summary[c] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Legend() {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-3">Status Codes</p>
      <div className="space-y-1.5">
        {STATUS_CODES.map(c => (
          <div key={c} className="flex items-center gap-3 text-xs">
            <span className="font-mono w-12 text-center px-1.5 py-0.5 rounded" style={{ color: STATUS_META[c].color, border: `1px solid ${STATUS_META[c].border}`, backgroundColor: STATUS_META[c].bg }}>{c}</span>
            <span className="text-foreground/90">{STATUS_META[c].label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
